
import { createRoot, hydrateRoot } from 'react-dom/client'
import App from './App.tsx'
import ErrorBoundary from './components/ErrorBoundary'
import '@/index.css';

declare global {
  interface Window {
    __DEBUG_OVERLAY__?: {
      log: (message: string, level?: 'log' | 'info' | 'warn' | 'error') => void;
    };
  }
}

// Check if we're running in a browser
const isBrowser = typeof window !== 'undefined';
const isDebugMode = isBrowser && /([?&])debug=1\b/.test(window.location.search);

const formatDebugArg = (arg: unknown) => {
  if (typeof arg === 'string') return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
};

const debugLog = (...args: unknown[]) => {
  if (!isDebugMode) return;
  const formatted = args.map(formatDebugArg).join(' ');
  console.log('[main]', ...args);
  try {
    window.__DEBUG_OVERLAY__?.log('[main] ' + formatted, 'info');
  } catch (err) {
    console.error('[main] Failed to push to debug overlay', err);
  }
};

// Check if the app is being hydrated (SSR) or rendered from scratch (CSR)
if (isBrowser) {
  debugLog('Browser detected. Beginning initialization.');
  try {
    const container = document.getElementById("root");
    debugLog('Root element lookup result:', !!container, container?.getAttribute('data-ssr') ? 'data-ssr present' : 'data-ssr missing');
    
    if (!container) {
      console.error('❌ Root element not found! Waiting for DOM...');
      debugLog('Root element not found immediately, waiting for DOMContentLoaded.');
      // Wait for DOM to be ready if root isn't available yet
      const initWhenReady = () => {
        const retryContainer = document.getElementById("root");
        if (retryContainer) {
          console.log('✅ Root element found after waiting');
          debugLog('Root element found after DOMContentLoaded. Proceeding.');
          initializeApp(retryContainer);
        } else {
          console.error('❌ Root element still not found after DOMContentLoaded');
          debugLog('Root element still missing after DOMContentLoaded.');
        }
      };
      
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initWhenReady);
      } else {
        initWhenReady();
      }
    } else {
      initializeApp(container);
    }
  } catch (error) {
    console.error('❌ Fatal error initializing app:', error);
    debugLog('Fatal initialization error:', error instanceof Error ? error.message : error);
    // Try to show error to user
    const root = document.getElementById("root");
    if (root) {
      root.innerHTML = '<div style="padding: 20px; background: #fee; color: #c00; font-family: sans-serif;"><h1>Application Error</h1><p>Failed to initialize the application. Please refresh the page.</p></div>';
    }
  }
}

function initializeApp(container: HTMLElement) {
  try {
    debugLog('initializeApp called. data-ssr:', container.hasAttribute('data-ssr'));
    const appWithErrorBoundary = (
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    );
    
    // Check if we're hydrating an existing server-rendered HTML
    if (container.hasAttribute('data-ssr')) {
      console.log('Hydrating server-rendered content');
      debugLog('Hydrating existing SSR markup.');
      // Hydrate existing HTML
      hydrateRoot(container, appWithErrorBoundary);
    } else {
      console.log('Client-side rendering');
      debugLog('Performing client-side render.');
      // Create a new root for client-side rendering
      createRoot(container).render(appWithErrorBoundary);
    }
    debugLog('initializeApp completed successfully.');
  } catch (error) {
    console.error('❌ Error during React initialization:', error);
    debugLog('Error during React initialization:', error instanceof Error ? error.message : error);
    container.innerHTML = '<div style="padding: 20px; background: #fee; color: #c00; font-family: sans-serif;"><h1>Rendering Error</h1><p>Failed to render the application. Please refresh the page.</p><pre>' + (error instanceof Error ? error.message : String(error)) + '</pre></div>';
  }
}

// Import the craft generator embedding functionality
import { embedCraftGenerator } from "@/components/tools/EmbeddableCraftGenerator";
// Import the scene generator embedding functionality
import { embedSceneGenerator } from "@/components/tools/EmbeddableSceneGenerator";
// Import the recipe generator embedding functionality
import { embedRecipeGenerator } from "@/components/tools/EmbeddableRecipeGenerator";
// Import the name generator embedding functionality
import { embedElfNameGenerator } from "@/components/tools/EmbeddableElfNameGenerator";

// Expose the embed functions globally for use in embedded blog posts
if (typeof window !== 'undefined') {
  (window as any).embedElfCraftGenerator = embedCraftGenerator;
  (window as any).embedElfSceneGenerator = embedSceneGenerator;
  (window as any).embedElfRecipeGenerator = embedRecipeGenerator;
  (window as any).embedElfNameGenerator = embedElfNameGenerator;
}

// Export for SSR
export function renderApp() {
  return <App />;
}
